import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Activity, UserPlus, Pencil, Trash2, Eye, FolderPlus, FolderOpen,
  Download, Upload, ChevronDown, Filter,
} from "lucide-react";

const EVENT_CONFIG = {
  contact_created: { icon: UserPlus, color: "text-emerald-600", bg: "bg-emerald-50" },
  contact_updated: { icon: Pencil, color: "text-blue-600", bg: "bg-blue-50" },
  contact_deleted: { icon: Trash2, color: "text-red-500", bg: "bg-red-50" },
  contacts_batch_deleted: { icon: Trash2, color: "text-red-500", bg: "bg-red-50" },
  visibility_changed: { icon: Eye, color: "text-violet-600", bg: "bg-violet-50" },
  group_created: { icon: FolderPlus, color: "text-teal-600", bg: "bg-teal-50" },
  group_updated: { icon: FolderOpen, color: "text-teal-600", bg: "bg-teal-50" },
  group_deleted: { icon: Trash2, color: "text-red-500", bg: "bg-red-50" },
  import_committed: { icon: Upload, color: "text-amber-600", bg: "bg-amber-50" },
  contacts_exported: { icon: Download, color: "text-zinc-600", bg: "bg-zinc-100" },
};

const FILTER_TABS = [
  { key: "all", label: "All" },
  { key: "changes", label: "Changes", types: ["contact_created", "contact_updated", "contact_deleted", "contacts_batch_deleted", "visibility_changed"] },
  { key: "groups", label: "Groups", types: ["group_created", "group_updated", "group_deleted"] },
  { key: "imports", label: "Imports", types: ["import_committed", "contacts_exported"] },
];

function formatEvent(log) {
  const { event_type, metadata } = log;
  const m = metadata || {};

  switch (event_type) {
    case "contact_created":
      return `Contact created: ${m.email || "unknown"}`;
    case "contact_updated":
      return `Contact updated${m.changed_fields?.length ? ` (${m.changed_fields.join(", ")})` : ""}`;
    case "contact_deleted":
      return "Contact deleted";
    case "contacts_batch_deleted":
      return `${m.count || 0} contact${(m.count || 0) !== 1 ? "s" : ""} deleted`;
    case "visibility_changed": {
      const vis = m.new_visibility?.replace(/_/g, " ") || "unknown";
      return `Visibility changed to ${vis}`;
    }
    case "group_created":
      return `Group created: ${m.name || "unknown"}`;
    case "group_updated":
      return `Group updated${m.changed_fields?.length ? ` (${m.changed_fields.join(", ")})` : ""}`;
    case "group_deleted":
      return "Group deleted";
    case "import_committed": {
      const total = m.total || 0;
      const parts = [];
      if (m.created) parts.push(`${m.created} new`);
      if (m.merged) parts.push(`${m.merged} merged`);
      if (m.skipped) parts.push(`${m.skipped} skipped`);
      return `Imported ${total} contacts${parts.length ? ` (${parts.join(", ")})` : ""}`;
    }
    case "contacts_exported":
      return `Exported ${m.result_count || 0} contacts as ${m.format || "CSV"}`;
    default:
      return event_type.replace(/_/g, " ");
  }
}

function formatTime(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
}

export default function ActivityFeed() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");

  const fetchLogs = useCallback(async (append = false, existingCursor = null) => {
    if (append) setLoadingMore(true);
    else setLoading(true);

    try {
      const params = new URLSearchParams();
      params.set("limit", "30");

      const tab = FILTER_TABS.find((t) => t.key === activeFilter);
      if (tab?.types) {
        params.set("entity_type", tab.key === "imports" ? "import" : tab.key === "groups" ? "group" : "contact");
      }

      if (append && existingCursor) {
        params.set("cursor", existingCursor);
      }

      const { data } = await api.get(`/activity-logs?${params}`);

      let filteredLogs = data.logs || [];
      if (tab?.types) {
        filteredLogs = filteredLogs.filter((l) => tab.types.includes(l.event_type));
      }

      if (append) {
        setLogs((prev) => [...prev, ...filteredLogs]);
      } else {
        setLogs(filteredLogs);
      }
      setHasMore(data.has_more);
      setCursor(data.next_cursor);
    } catch {
      // silent
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    fetchLogs(false);
  }, [fetchLogs]);

  const handleLoadMore = () => {
    if (cursor && hasMore) fetchLogs(true, cursor);
  };

  return (
    <div className="h-full flex flex-col" data-testid="activity-feed">
      {/* Filter tabs */}
      <div className="flex items-center gap-1.5 px-1 pb-3" data-testid="activity-filter-tabs">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveFilter(tab.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeFilter === tab.key
                ? "bg-zinc-900 text-white"
                : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-700"
            }`}
            data-testid={`activity-filter-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Event list */}
      <div className="flex-1 overflow-y-auto -mx-1">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-5 h-5 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-center" data-testid="activity-empty">
            <Activity size={28} className="text-zinc-300 mb-2" />
            <p className="text-xs text-zinc-500">No activity yet</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {logs.map((log) => {
              const config = EVENT_CONFIG[log.event_type] || { icon: Activity, color: "text-zinc-500", bg: "bg-zinc-100" };
              const Icon = config.icon;
              return (
                <div
                  key={log.log_id}
                  className="flex items-start gap-3 px-2 py-2.5 rounded-lg hover:bg-zinc-50 transition-colors group"
                  data-testid={`activity-event-${log.log_id}`}
                >
                  <div className={`w-7 h-7 rounded-full ${config.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                    <Icon size={13} className={config.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-800 leading-snug">
                      {formatEvent(log)}
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      {formatTime(log.created_at)}
                    </p>
                  </div>
                  <Badge
                    variant="outline"
                    className="text-[9px] px-1.5 py-0 font-normal text-zinc-500 border-zinc-200 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    {log.entity_type}
                  </Badge>
                </div>
              );
            })}

            {hasMore && (
              <div className="pt-2 pb-1 flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="text-xs text-zinc-500"
                  data-testid="activity-load-more"
                >
                  {loadingMore ? (
                    <div className="w-3.5 h-3.5 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin mr-1.5" />
                  ) : (
                    <ChevronDown size={13} className="mr-1.5" />
                  )}
                  Load more
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
